import { SensorType, SensorSpec } from "../types/sensors";

// Sensor capabilities
export const SENSOR_CAPABILITIES = {
  LIDAR: "Lidar",
  RADAR: "Radar",
  JAMMERS: "Jammers",
  DENSITOMETER: "Densitometer",
  NEURAL_ACTIVITY: "Neural Activity Sensor",
} as const;

// Sensor types and specifications
export const SENSOR_TYPES: Record<SensorType, SensorSpec> = {
  BASIC: {
    type: "BASIC",
    name: "Basic Sensors",
    description: "Standard sensor suite with Lidar and Radar",
    capabilities: [SENSOR_CAPABILITIES.LIDAR, SENSOR_CAPABILITIES.RADAR],
    dm: -4,
    power: 0,
    tons: 0,
    cost: 0,
    techLevel: 8,
    maintenanceCost: 0, // No maintenance cost for basic sensors
    special: null,
  },
  CIVILIAN: {
    type: "CIVILIAN",
    name: "Civilian Grade Sensors",
    description: "Enhanced sensor suite with improved Lidar and Radar",
    capabilities: [SENSOR_CAPABILITIES.LIDAR, SENSOR_CAPABILITIES.RADAR],
    dm: -2,
    power: 1,
    tons: 1,
    cost: 3_000_000, // MCr3
    techLevel: 9,
    maintenanceCost: 0.01, // 1% of cost per month
    special: null,
  },
  MILITARY: {
    type: "MILITARY",
    name: "Military Grade Sensors",
    description: "Advanced sensor suite with jamming capabilities",
    capabilities: [
      SENSOR_CAPABILITIES.JAMMERS,
      SENSOR_CAPABILITIES.LIDAR,
      SENSOR_CAPABILITIES.RADAR,
    ],
    dm: 0,
    power: 2,
    tons: 2,
    cost: 4_100_000, // MCr4.1
    techLevel: 10,
    maintenanceCost: 0.015, // 1.5% of cost per month
    special: "Jamming capabilities",
  },
  IMPROVED: {
    type: "IMPROVED",
    name: "Improved Sensors",
    description: "High-tech sensor suite with densitometer",
    capabilities: [
      SENSOR_CAPABILITIES.DENSITOMETER,
      SENSOR_CAPABILITIES.JAMMERS,
      SENSOR_CAPABILITIES.LIDAR,
      SENSOR_CAPABILITIES.RADAR,
    ],
    dm: 1,
    power: 4,
    tons: 3,
    cost: 4_300_000, // MCr4.3
    techLevel: 12,
    maintenanceCost: 0.02, // 2% of cost per month
    special: "Densitometer capabilities",
  },
  ADVANCED: {
    type: "ADVANCED",
    name: "Advanced Sensors",
    description: "Cutting-edge sensor suite with neural activity detection",
    capabilities: [
      SENSOR_CAPABILITIES.DENSITOMETER,
      SENSOR_CAPABILITIES.JAMMERS,
      SENSOR_CAPABILITIES.LIDAR,
      SENSOR_CAPABILITIES.NEURAL_ACTIVITY,
      SENSOR_CAPABILITIES.RADAR,
    ],
    dm: 2,
    power: 6,
    tons: 5,
    cost: 5_300_000, // MCr5.3
    techLevel: 15,
    maintenanceCost: 0.025, // 2.5% of cost per month
    special: "Neural activity detection",
  },
} as const;

// Helper functions
export const getSensorSpec = (type: SensorType): SensorSpec => {
  return SENSOR_TYPES[type];
};

export const calculateSensorCost = (
  type: SensorType,
  shipTons: number,
): number => {
  const spec = getSensorSpec(type);
  console.log("🔧 calculateSensorCost DEBUG:");
  console.log("  - type:", type);
  console.log("  - shipTons:", shipTons);
  console.log("  - spec:", spec);
  console.log("  - spec.cost:", spec.cost);
  
  // Only scale for special systems (not standard suites)
  if (
    ["BASIC", "CIVILIAN", "MILITARY", "IMPROVED", "ADVANCED"].includes(type)
  ) {
    console.log("  - Using base cost (no scaling):", spec.cost);
    return spec.cost;
  }
  const scaledCost = spec.cost * (shipTons / 100);
  console.log("  - Using scaled cost:", scaledCost);
  return scaledCost;
};

export const calculateSensorTons = (
  type: SensorType,
  shipTons: number,
): number => {
  const spec = getSensorSpec(type);
  if (
    ["BASIC", "CIVILIAN", "MILITARY", "IMPROVED", "ADVANCED"].includes(type)
  ) {
    return spec.tons;
  }
  return spec.tons * (shipTons / 100);
};

export const calculateSensorPower = (
  type: SensorType,
  shipTons: number,
): number => {
  const spec = getSensorSpec(type);
  if (
    ["BASIC", "CIVILIAN", "MILITARY", "IMPROVED", "ADVANCED"].includes(type)
  ) {
    return spec.power;
  }
  return spec.power * (shipTons / 100);
};

// Validation functions
export const validateSensorSpec = (spec: SensorSpec): string[] => {
  const errors: string[] = [];

  if (!spec.type) {
    errors.push("Sensor type is required");
  }

  if (!spec.name) {
    errors.push("Sensor name is required");
  }

  if (spec.cost < 0) {
    errors.push("Cost cannot be negative");
  }

  if (spec.tons < 0) {
    errors.push("Tons cannot be negative");
  }

  if (spec.power < 0) {
    errors.push("Power requirement cannot be negative");
  }

  if (spec.techLevel < 0) {
    errors.push("Tech level cannot be negative");
  }

  if (!Array.isArray(spec.capabilities)) {
    errors.push("Capabilities must be an array");
  }

  return errors;
};

export const validateSensorForShip = (
  type: SensorType,
  shipTons: number,
  techLevel: number,
): string[] => {
  const errors: string[] = [];
  const spec = getSensorSpec(type);

  if (techLevel < spec.techLevel) {
    errors.push(
      `Ship tech level (${techLevel}) is too low for ${spec.name} (requires TL${spec.techLevel})`,
    );
  }

  if (shipTons < 10) {
    errors.push("Ship must be at least 10 tons to mount sensors");
  }

  return errors;
};

export { SENSOR_TYPES as SENSORS };
